import React, { useCallback, useEffect, useState } from 'react';
import {
    Box,
    Card,
    CardContent,
    CardHeader,
    Typography,
    TextField,
    Button,
    Grid,
    Divider,
    Alert,
    Chip,
    List,
    ListItem,
    ListItemText,
    Paper,
} from '@mui/material';
import { useAuth } from '../context/AuthContext';
import { apiClient } from '../services/apiClient';
import type { MFASetupBeginResponse, MFASetupVerifyResponse, MFAStatusResponse } from '../services/types';

const ProfilePage: React.FC = () => {
    const { user, updateProfile, changePassword } = useAuth();

    // Profile Form State
    const [firstName, setFirstName] = useState(user?.first_name || '');
    const [lastName, setLastName] = useState(user?.last_name || '');
    const [email, setEmail] = useState(user?.email || '');
    const [profileSaving, setProfileSaving] = useState(false);
    const [profileAlert, setProfileAlert] = useState<{ type: 'success' | 'error'; message: string } | null>(null);

    // Password Form State
    const [currentPassword, setCurrentPassword] = useState('');
    const [newPassword, setNewPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [passwordSaving, setPasswordSaving] = useState(false);
    const [passwordAlert, setPasswordAlert] = useState<{ type: 'success' | 'error'; message: string } | null>(null);

    // MFA state
    const [mfaStatus, setMfaStatus] = useState<MFAStatusResponse | null>(null);
    const [mfaLoading, setMfaLoading] = useState(false);
    const [mfaAlert, setMfaAlert] = useState<{ type: 'success' | 'error' | 'info'; message: string } | null>(null);
    const [mfaSetup, setMfaSetup] = useState<MFASetupBeginResponse | null>(null);
    const [mfaVerifyCode, setMfaVerifyCode] = useState('');
    const [mfaActionCode, setMfaActionCode] = useState('');
    const [backupCodes, setBackupCodes] = useState<string[]>([]);

    const loadMfaStatus = useCallback(async () => {
        try {
            const res = await apiClient.get<MFAStatusResponse>('/api/v1/auth/mfa/status');
            setMfaStatus(res.data);
        } catch {
            setMfaStatus(null);
        }
    }, []);

    useEffect(() => {
        void loadMfaStatus();
    }, [loadMfaStatus]);

    const handleProfileSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setProfileSaving(true);
        setProfileAlert(null);
        const result = await updateProfile({ first_name: firstName, last_name: lastName, email });
        setProfileSaving(false);
        if (result.success) {
            setProfileAlert({ type: 'success', message: 'Profile updated successfully.' });
        } else {
            setProfileAlert({ type: 'error', message: result.error || 'Failed to update profile.' });
        }
    };

    const handlePasswordSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        if (newPassword !== confirmPassword) {
            setPasswordAlert({ type: 'error', message: 'New passwords do not match.' });
            return;
        }
        setPasswordSaving(true);
        setPasswordAlert(null);
        const result = await changePassword({ current_password: currentPassword, new_password: newPassword });
        setPasswordSaving(false);
        if (result.success) {
            setPasswordAlert({ type: 'success', message: 'Password changed successfully.' });
            setCurrentPassword('');
            setNewPassword('');
            setConfirmPassword('');
        } else {
            setPasswordAlert({ type: 'error', message: result.error || 'Failed to change password.' });
        }
    };

    const handleMfaSetupBegin = async () => {
        setMfaLoading(true);
        setMfaAlert(null);
        try {
            const res = await apiClient.post<MFASetupBeginResponse>('/api/v1/auth/mfa/setup');
            setMfaSetup(res.data);
            setBackupCodes([]);
            setMfaAlert({ type: 'info', message: 'Scan the QR code, then enter a 6-digit code to verify setup.' });
        } catch (err: any) {
            setMfaAlert({ type: 'error', message: err?.response?.data?.detail || 'Failed to start MFA setup.' });
        } finally {
            setMfaLoading(false);
        }
    };

    const handleMfaSetupVerify = async (e: React.FormEvent) => {
        e.preventDefault();
        setMfaLoading(true);
        setMfaAlert(null);
        try {
            const res = await apiClient.post<MFASetupVerifyResponse>('/api/v1/auth/mfa/setup/verify', {
                code: mfaVerifyCode,
            });
            setBackupCodes(res.data.backup_codes || []);
            setMfaSetup(null);
            setMfaVerifyCode('');
            setMfaAlert({ type: 'success', message: 'MFA enabled. Save your backup codes in a safe place.' });
            await loadMfaStatus();
        } catch (err: any) {
            setMfaAlert({ type: 'error', message: err?.response?.data?.detail || 'Invalid MFA code.' });
        } finally {
            setMfaLoading(false);
        }
    };

    const handleRegenerateBackupCodes = async () => {
        if (!mfaActionCode.trim()) {
            setMfaAlert({ type: 'error', message: 'Enter your current authenticator code first.' });
            return;
        }
        setMfaLoading(true);
        setMfaAlert(null);
        try {
            const res = await apiClient.post<MFASetupVerifyResponse>('/api/v1/auth/mfa/backup-codes/regenerate', {
                code: mfaActionCode.trim(),
            });
            setBackupCodes(res.data.backup_codes || []);
            setMfaActionCode('');
            setMfaAlert({ type: 'success', message: 'Backup codes regenerated.' });
            await loadMfaStatus();
        } catch (err: any) {
            setMfaAlert({ type: 'error', message: err?.response?.data?.detail || 'Could not regenerate backup codes.' });
        } finally {
            setMfaLoading(false);
        }
    };

    const handleDisableMfa = async () => {
        if (!mfaActionCode.trim()) {
            setMfaAlert({ type: 'error', message: 'Enter your current authenticator code first.' });
            return;
        }
        setMfaLoading(true);
        setMfaAlert(null);
        try {
            await apiClient.post('/api/v1/auth/mfa/disable', { code: mfaActionCode.trim() });
            setMfaSetup(null);
            setBackupCodes([]);
            setMfaActionCode('');
            setMfaAlert({ type: 'success', message: 'MFA disabled.' });
            await loadMfaStatus();
        } catch (err: any) {
            setMfaAlert({ type: 'error', message: err?.response?.data?.detail || 'Could not disable MFA.' });
        } finally {
            setMfaLoading(false);
        }
    };

    return (
        <Box sx={{ maxWidth: 800, mx: 'auto', mt: 4 }}>
            <Typography variant="h4" fontWeight={600} mb={4}>
                Account Settings
            </Typography>

            <Grid container spacing={4}>
                {/* Profile Details */}
                <Grid item xs={12}>
                    <Card elevation={1}>
                        <CardHeader title="Personal Information" subheader="Update your basic profile details here." />
                        <Divider />
                        <CardContent>
                            {profileAlert && (
                                <Alert severity={profileAlert.type} sx={{ mb: 3 }}>
                                    {profileAlert.message}
                                </Alert>
                            )}
                            <form onSubmit={handleProfileSubmit}>
                                <Grid container spacing={3}>
                                    <Grid item xs={12} sm={6}>
                                        <TextField
                                            fullWidth
                                            label="First Name"
                                            value={firstName}
                                            onChange={(e) => setFirstName(e.target.value)}
                                            required
                                        />
                                    </Grid>
                                    <Grid item xs={12} sm={6}>
                                        <TextField
                                            fullWidth
                                            label="Last Name"
                                            value={lastName}
                                            onChange={(e) => setLastName(e.target.value)}
                                        />
                                    </Grid>
                                    <Grid item xs={12}>
                                        <TextField
                                            fullWidth
                                            type="email"
                                            label="Email Address"
                                            value={email}
                                            onChange={(e) => setEmail(e.target.value)}
                                            required
                                        />
                                    </Grid>
                                    <Grid item xs={12} display="flex" justifyContent="flex-end">
                                        <Button variant="contained" type="submit" disabled={profileSaving}>
                                            {profileSaving ? 'Saving...' : 'Save Changes'}
                                        </Button>
                                    </Grid>
                                </Grid>
                            </form>
                        </CardContent>
                    </Card>
                </Grid>

                {/* Change Password */}
                <Grid item xs={12}>
                    <Card elevation={1}>
                        <CardHeader title="Security" subheader="Update your password." />
                        <Divider />
                        <CardContent>
                            {passwordAlert && (
                                <Alert severity={passwordAlert.type} sx={{ mb: 3 }}>
                                    {passwordAlert.message}
                                </Alert>
                            )}
                            <form onSubmit={handlePasswordSubmit}>
                                <Grid container spacing={3}>
                                    <Grid item xs={12}>
                                        <TextField
                                            fullWidth
                                            type="password"
                                            label="Current Password"
                                            value={currentPassword}
                                            onChange={(e) => setCurrentPassword(e.target.value)}
                                            required
                                        />
                                    </Grid>
                                    <Grid item xs={12} sm={6}>
                                        <TextField
                                            fullWidth
                                            type="password"
                                            label="New Password"
                                            value={newPassword}
                                            onChange={(e) => setNewPassword(e.target.value)}
                                            required
                                        />
                                    </Grid>
                                    <Grid item xs={12} sm={6}>
                                        <TextField
                                            fullWidth
                                            type="password"
                                            label="Confirm New Password"
                                            value={confirmPassword}
                                            onChange={(e) => setConfirmPassword(e.target.value)}
                                            required
                                        />
                                    </Grid>
                                    <Grid item xs={12} display="flex" justifyContent="flex-end">
                                        <Button variant="contained" color="secondary" type="submit" disabled={passwordSaving}>
                                            {passwordSaving ? 'Updating...' : 'Update Password'}
                                        </Button>
                                    </Grid>
                                </Grid>
                            </form>
                        </CardContent>
                    </Card>
                </Grid>

                {/* MFA */}
                <Grid item xs={12}>
                    <Card elevation={1}>
                        <CardHeader title="Multi-factor authentication (MFA)" subheader="Protect your account with TOTP codes and backup codes." />
                        <Divider />
                        <CardContent>
                            {mfaAlert && (
                                <Alert severity={mfaAlert.type} sx={{ mb: 3 }}>
                                    {mfaAlert.message}
                                </Alert>
                            )}

                            <Box sx={{ display: 'flex', gap: 1, mb: 2, flexWrap: 'wrap' }}>
                                <Chip
                                    color={mfaStatus?.enabled ? 'success' : 'default'}
                                    label={mfaStatus?.enabled ? 'MFA enabled' : 'MFA not enabled'}
                                />
                                {mfaStatus?.enforced_for_tenant && (
                                    <Chip color="warning" label="Enforced by organization policy" />
                                )}
                                {typeof mfaStatus?.backup_codes_remaining === 'number' && (
                                    <Chip label={`Backup codes: ${mfaStatus.backup_codes_remaining}`} />
                                )}
                            </Box>

                            {!mfaStatus?.enabled && (
                                <Button
                                    variant="contained"
                                    onClick={handleMfaSetupBegin}
                                    disabled={mfaLoading}
                                    sx={{ mb: 2 }}
                                >
                                    {mfaLoading ? 'Preparing…' : 'Set up MFA'}
                                </Button>
                            )}

                            {mfaSetup && (
                                <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
                                    <Typography variant="subtitle2" sx={{ mb: 1 }}>
                                        Scan this QR code in Google Authenticator / Authy
                                    </Typography>
                                    <Box
                                        component="img"
                                        src={mfaSetup.qr_image_data_url}
                                        alt="MFA QR code"
                                        sx={{ width: 180, height: 180, border: '1px solid #e5e7eb', borderRadius: 1, mb: 1 }}
                                    />
                                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                                        Manual key: <strong>{mfaSetup.secret}</strong>
                                    </Typography>
                                    <form onSubmit={handleMfaSetupVerify}>
                                        <TextField
                                            fullWidth
                                            label="Verification code"
                                            value={mfaVerifyCode}
                                            onChange={(e) => setMfaVerifyCode(e.target.value)}
                                            sx={{ mb: 2 }}
                                        />
                                        <Button type="submit" variant="contained" disabled={mfaLoading || !mfaVerifyCode.trim()}>
                                            Verify and enable
                                        </Button>
                                    </form>
                                </Paper>
                            )}

                            {mfaStatus?.enabled && (
                                <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
                                    <Typography variant="subtitle2" sx={{ mb: 1 }}>
                                        Manage MFA
                                    </Typography>
                                    <TextField
                                        fullWidth
                                        label="Current authenticator code"
                                        value={mfaActionCode}
                                        onChange={(e) => setMfaActionCode(e.target.value)}
                                        sx={{ mb: 2 }}
                                    />
                                    <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                                        <Button variant="outlined" onClick={handleRegenerateBackupCodes} disabled={mfaLoading}>
                                            Regenerate backup codes
                                        </Button>
                                        <Button
                                            variant="outlined"
                                            color="error"
                                            onClick={handleDisableMfa}
                                            disabled={mfaLoading || !!mfaStatus?.enforced_for_tenant}
                                        >
                                            Disable MFA
                                        </Button>
                                    </Box>
                                </Paper>
                            )}

                            {backupCodes.length > 0 && (
                                <Paper variant="outlined" sx={{ p: 2 }}>
                                    <Typography variant="subtitle2" sx={{ mb: 1 }}>
                                        Backup codes (shown once)
                                    </Typography>
                                    <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                                        Save these somewhere safe. Each code can be used once.
                                    </Typography>
                                    <List dense>
                                        {backupCodes.map((code) => (
                                            <ListItem key={code} disableGutters>
                                                <ListItemText primary={code} />
                                            </ListItem>
                                        ))}
                                    </List>
                                </Paper>
                            )}
                        </CardContent>
                    </Card>
                </Grid>
            </Grid>
        </Box>
    );
};

export default ProfilePage;
